<template>
  <BlBackdrop
    label="Update"
    :submitLoading="submitLoading"
    :disabled="isDisabledSubmit"
    @backdrop-close="$emit('close-modal')"
    @backdrop-action="onSubmit"
  >
    <div :style="{ width: '45rem', maxHeight: 'calc(100vh - 16rem)' }">
      <BlTextTitle4 text="Edit task" class="px-8 pt-10 mb-8" />
      <div
        class="flex flex-col gap-4 px-8 pb-4 overflow-y-auto"
        style="max-height: calc(100vh - 23rem);"
      >
        <div v-for="item in defaultTasks" :key="item.id" class="">
          <div class="flex items-center gap-2 ">
            <div class="w-full pb-3 border-b">
              <BlTextBody2
                :text="`${item.code} - ${item.title}`"
                class="text-slate-1000"
              />
            </div>
            <div
              class="flex-shrink-0 w-5 pb-3"
              @click="!item.is_used && deleteTask(item)"
            >
              <BlIcon
                :icon="!item.is_used ? 'delete' : 'delete'"
                :color="!item.is_used ? 'error' : 'grey'"
                class="w-5 h-5"
                :class="!item.is_used ? 'cursor-pointer' : 'cursor-not-allowed'"
                :tooltip="
                  item.is_used
                    ? 'This task has recorded data and cannot be deleted'
                    : ''
                "
                tooltip-position="bottom-left"
              />
            </div>
          </div>
        </div>
        <div v-for="newTask in newTasks" :key="newTask.key" class="">
          <div class="flex flex-col gap-2 pb-3 border-b">
            <div class="flex items-center w-full gap-4">
              <BlSelectBox
                :options="getFilteredCodeOptions(newTask.key)"
                class="flex-shrink-0 w-32"
                v-model="taskCodeSelections[newTask.key]"
                @change="value => onChangeCode(value, newTask.key)"
                placeholder="Code"
                position="bottom"
                :key="
                  `select-${newTask.key}-${selectBoxKeys[newTask.key] || 0}`
                "
              />
              <BlInputText
                :input="getTaskInput(newTask.key).titleInput"
                class="w-full"
                @changed="
                  payload => inputHandler(payload, newTask.key, 'title')
                "
              />
              <div
                class="relative flex items-center justify-between flex-shrink-0 w-16 px-4 border rounded cursor-pointer bg-pure-white border-grey-200 h-11"
              >
                <div
                  class="w-20 h-1"
                  :style="{
                    backgroundColor: newTask.color || '#2AB1A8'
                  }"
                />
              </div>
              <div class="flex-shrink-0 w-5"></div>
            </div>
            <div class="flex items-start gap-2">
              <BlInputText
                :input="getTaskInput(newTask.key).descriptionInput"
                @changed="
                  payload => inputHandler(payload, newTask.key, 'description')
                "
              />
              <div class="" @click="deleteNewTask(newTask.key)">
                <BlIcon
                  icon="delete"
                  color="error"
                  class="w-5 h-5 cursor-pointer"
                />
              </div>
            </div>
          </div>
        </div>
        <BlInputButton
          v-if="!allTasksUsed"
          label="Add task"
          icon="plus"
          class="self-start px-0"
          type="plain"
          @clicked="onAddTasks"
        />
        <BlTooltip v-if="allTasksUsed" style="width: fit-content;" drop="top" tooltip="You've reached the limit for tasks and can't create a new one.">
          <BlInputButton
            label="Add task"
            icon="plus"
            class="self-start px-2 cursor-not-allowed"
            type="plain"
            disabled
          />
        </BlTooltip>
      </div>
    </div>
  </BlBackdrop>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import BlBackdrop from "../../../BlBackdrop.vue";
import BlIcon from "../../../BlIcon.vue";
import BlTextTitle4 from "../../../BlTextTitle4.vue";
import BlTextBody2 from "../../../BlTextBody2.vue";
import BlInputButton from "../../../BlInputButton.vue";
import BlSelectBox from "../../../BlSelectBox.vue";
import BlInputText from "../../../BlInputText.vue";
import BlTooltip from "../../../BlTooltip.vue";

export default {
  name: "EditTargetTaskDetailsModal",
  components: {
    BlBackdrop,
    BlIcon,
    BlTextTitle4,
    BlTextBody2,
    BlInputButton,
    BlSelectBox,
    BlInputText,
    BlTooltip
  },
  props: ["tasks", "target"],
  data() {
    return {
      submitLoading: false,
      defaultTasks: [],
      newTasks: [],
      softDeleteTasks: [],
      loadingTasks: false,
      taskCodeSelections: {},
      taskInputs: {},
      selectBoxKeys: {},
      defaultTitleInput: {
        placeholder: "Type task title",
        model: ""
      },
      defaultDescriptionInput: {
        placeholder: "Type description",
        model: "",
        textarea: true
      }
    };
  },
  watch: {
    watch: {
      defaultTasks: {
        handler() {
          this.$nextTick(() => {
            this.$forceUpdate();
          });
        },
        deep: true
      },
      newTasks: {
        handler() {
          this.$nextTick(() => {
            this.$forceUpdate();
          });
        },
        deep: true
      },
      softDeleteTasks: {
        handler() {
          this.$nextTick(() => {
            this.$forceUpdate();
          });
        },
        deep: true
      }
    }
  },
  computed: {
    ...mapGetters(["tasksCodes"]),
    isDisabledSubmit() {
      if (this.newTasks.length > 0) {
        return this.newTasks.some(task => {
          const codeSelection = this.taskCodeSelections[task.key];
          return !codeSelection || codeSelection === "" || !task.title;
        });
      }
      return false;
    },
    codeOptions() {
      if (!this.tasksCodes || !this.tasksCodes.length) {
        return [];
      }
      return this.tasksCodes.map(i => ({
        value: i.id,
        label: i.code
      }));
    },
    allTasksUsed() {
      const totalTasksUsed = this.defaultTasks.length + this.newTasks.length;
      return totalTasksUsed >= this.tasksCodes.length;
    },
    usedCodeList() {
    // Get codes from defaultTasks (excluding soft deleted tasks)
      const defaultTaskCodes = this.defaultTasks
        .map(task => task.code ? task.code.toUpperCase() : "")
        .filter(code => code);

      // Get codes from newTasks with selected codes
      const newTaskCodes = Object.keys(this.taskCodeSelections)
        .filter(key => this.taskCodeSelections[key]) // Only tasks with selected codes
        .map(key => {
          const codeId = this.taskCodeSelections[key];
          const codeObj = this.tasksCodes.find(c => c.id === codeId);
          return codeObj ? codeObj.code.toUpperCase() : "";
        })
        .filter(code => code);

      // Get codes from soft deleted tasks
      const softDeletedCodes = this.softDeleteTasks
        .map(task => task.code ? task.code.toUpperCase() : "")
        .filter(code => code);

      // Combine and filter out soft deleted codes
      return [...new Set([...defaultTaskCodes, ...newTaskCodes])]
        .filter(code => !softDeletedCodes.includes(code));
    }
  },
  mounted() {
    this.fetchTaskCodes();
    this.defaultTasks = [...this.tasks];
    if (this.tasks && this.tasks.length) {
      this.tasks.forEach(task => {
        this.$set(this.selectBoxKeys, task.id, 0);
      });
    }
    this.sortTasksByPosition();
  },
  methods: {
    ...mapActions(["showToast", "getTasksCodes", "updateTarget"]),

    getCurrentUsedCodes() {
      const defaultTaskCodes = this.defaultTasks.map(task => {
        const codeObj = this.tasksCodes.find(c => c.id === task.task_code_id || (task.code && c.code === task.code));
        return codeObj ? codeObj.code.toUpperCase() : "";
      }).filter(code => code);

      const newTaskCodes = [];
      for (const key in this.taskCodeSelections) {
        const codeId = this.taskCodeSelections[key];
        if (codeId) {
          const codeObj = this.tasksCodes.find(c => c.id === codeId);
          if (codeObj) {
            newTaskCodes.push(codeObj.code.toUpperCase());
          }
        }
      }

      const deletedCodes = this.softDeleteTasks.map(task => {
        if (task.code) return task.code.toUpperCase();
        const codeObj = this.tasksCodes.find(c => c.id === task.task_code_id);
        return codeObj ? codeObj.code.toUpperCase() : "";
      }).filter(code => code);

      const allCodes = [...new Set([...defaultTaskCodes, ...newTaskCodes])];
      return allCodes.filter(code => !deletedCodes.includes(code));
    },
    getFilteredCodeOptions(currentTaskKey) {
      if (!this.tasksCodes || !this.tasksCodes.length) {
        return [];
      }

      let currentCode = "";
      const currentSelection = this.taskCodeSelections[currentTaskKey];
      if (currentSelection) {
        const codeObj = this.tasksCodes.find(c => c.id === currentSelection);
        if (codeObj) {
          currentCode = codeObj.code.toUpperCase();
        }
      }

      const usedCodes = [];

      this.defaultTasks.forEach(task => {
        const codeObj = this.tasksCodes.find(c => c.id === task.task_code_id || (task.code && c.code === task.code));
        if (codeObj) usedCodes.push(codeObj.code.toUpperCase());
      });

      Object.keys(this.taskCodeSelections).forEach(key => {
        if (key !== currentTaskKey && this.taskCodeSelections[key]) {
          const codeObj = this.tasksCodes.find(c => c.id === this.taskCodeSelections[key]);
          if (codeObj) usedCodes.push(codeObj.code.toUpperCase());
        }
      });

      return this.tasksCodes.map(codeObj => {
        const codeUpperCase = codeObj.code.toUpperCase();
        const isUsed = usedCodes.includes(codeUpperCase) && codeUpperCase !== currentCode;

        return {
          value: codeObj.id,
          label: codeObj.code,
          disabled: isUsed
        };
      });
    },

    async fetchTaskCodes() {
      this.loadingTasks = true;
      await this.getTasksCodes();
      this.loadingTasks = false;
    },

    getTaskInput(key) {
      if (!this.taskInputs[key]) {
        this.$set(this.taskInputs, key, {
          titleInput: {
            placeholder: "Type task title",
            model: ""
          },
          descriptionInput: {
            placeholder: "Type description",
            model: "",
            textarea: true
          }
        });
      }
      return this.taskInputs[key];
    },

    inputHandler(payload, taskKey, inputType) {
      const taskInput = this.getTaskInput(taskKey);

      if (inputType === "title") {
        taskInput.titleInput.model = payload.value;
      } else if (inputType === "description") {
        taskInput.descriptionInput.model = payload.value;
      }

      this.updateTaskFromInputs(taskKey);
    },

    updateTaskFromInputs(taskKey) {
      const taskIndex = this.newTasks.findIndex(task => task.key === taskKey);
      if (taskIndex > -1) {
        const taskInput = this.getTaskInput(taskKey);
        const updatedTask = {
          ...this.newTasks[taskIndex],
          title: taskInput.titleInput.model,
          description: taskInput.descriptionInput.model
        };

        this.onUpdate(updatedTask);
      }
    },

    onAddTasks() {
      if (this.allTasksUsed) {
        this.showToast({
          title: "Maximum Tasks Reached",
          message: "You've reached the limit for tasks and can't create a new one.",
          type: "error"
        });
        return;
      }

      let length = 0;
      if (this.defaultTasks && this.defaultTasks.length) {
        if (this.newTasks && this.newTasks.length) {
          const maxPosition = Math.max(...this.newTasks.map(task => task.position));
          if (maxPosition > this.defaultTasks.length + this.newTasks.length) {
            length = maxPosition;
          } else {
            length = this.defaultTasks.length + this.newTasks.length;
          }
        } else {
          length = this.defaultTasks.length;
        }
      }

      function getRandomId() {
        const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
        let combination = "";
        for (let i = 0; i < 6; i++) {
          combination += chars[Math.round(Math.random() * 35)];
        }
        return `target-task-code-${combination}`;
      }

      const taskKey = getRandomId();
      const newTask = {
        key: taskKey,
        id: 0,
        code: "",
        title: "",
        code_definition: "",
        description: "",
        color: "#2AB1A8",
        position: length + 1
      };

      this.$set(this.taskCodeSelections, taskKey, "");
      this.$set(this.selectBoxKeys, taskKey, 0);
      this.$set(this.taskInputs, taskKey, {
        titleInput: {
          placeholder: "Type task title",
          model: ""
        },
        descriptionInput: {
          placeholder: "Type description",
          model: "",
          textarea: true
        }
      });

      const newArr = [...this.newTasks];
      newArr.push(newTask);
      this.newTasks = newArr;
      this.sortTasksByPosition();
    },

    isCodeAlreadyUsed(codeId, excludeTaskKey) {
      const codeObj = this.tasksCodes.find(c => c.id === codeId);
      if (!codeObj) return false;

      const codeUpperCase = codeObj.code.toUpperCase();

      let currentCode = "";
      const currentSelection = this.taskCodeSelections[excludeTaskKey];
      if (currentSelection) {
        const currentCodeObj = this.tasksCodes.find(c => c.id === currentSelection);
        if (currentCodeObj) {
          currentCode = currentCodeObj.code.toUpperCase();
        }
      }

      const usedCodes = this.usedCodeList.filter(code =>
        code !== (currentCode === codeUpperCase ? currentCode : "")
      );

      return usedCodes.includes(codeUpperCase);
    },

    onChangeCode(selectedValue, taskKey) {
      if (!selectedValue) return;

      const findCode = this.tasksCodes.find(i => i.id === selectedValue);
      if (!findCode) return;

      let currentCode = "";
      const currentSelection = this.taskCodeSelections[taskKey];
      if (currentSelection) {
        const codeObj = this.tasksCodes.find(c => c.id === currentSelection);
        if (codeObj) {
          currentCode = codeObj.code.toUpperCase();
        }
      }

      const usedCodes = this.getCurrentUsedCodes()
        .filter(code => code !== currentCode);

      if (usedCodes.includes(findCode.code.toUpperCase())) {
        this.showToast({
          title: "Kode Duplikat",
          message: `Kode ${findCode.code} sudah digunakan. Silakan pilih kode lain.`,
          type: "error"
        });

        this.$set(this.taskCodeSelections, taskKey, "");
        this.$set(this.selectBoxKeys, taskKey, (this.selectBoxKeys[taskKey] || 0) + 1);
        return;
      }

      this.$set(this.taskCodeSelections, taskKey, selectedValue);

      const taskIndex = this.newTasks.findIndex(task => task.key === taskKey);
      if (taskIndex > -1) {
        const updatedTask = {
          ...this.newTasks[taskIndex],
          id: findCode.id,
          code: findCode.code,
          code_definition: findCode.code_definition,
          color: findCode.color,
          position: findCode.position
        };

        this.$set(this.newTasks, taskIndex, updatedTask);
        this.onUpdate(updatedTask);
        this.sortTasksByPosition();

        this.$nextTick(() => {
          Object.keys(this.selectBoxKeys).forEach(key => {
            if (key !== taskKey) {
              this.$set(this.selectBoxKeys, key, (this.selectBoxKeys[key] || 0) + 1);
            }
          });
        });
      }
    },

    onUpdate(updatedTask) {
      const index = this.newTasks.findIndex(i => i.key === updatedTask.key);
      if (index > -1) {
        this.$set(this.newTasks, index, updatedTask);
      }
    },

    deleteTask(task) {
      const index = this.defaultTasks.findIndex(item => item.id === task.id);
      if (index > -1) {
        this.defaultTasks.splice(index, 1);
      }

      // Tambahkan ke softDeleteTasks dengan kode
      this.softDeleteTasks.push({
        id: task.id,
        _destroy: "1",
        code: task.code
      });

      // Force update semua dropdown untuk merefleksikan kode yang baru tersedia
      this.$nextTick(() => {
        Object.keys(this.selectBoxKeys).forEach(key => {
          this.$set(this.selectBoxKeys, key, (this.selectBoxKeys[key] || 0) + 1);
        });
      });

      this.sortTasksByPosition();
    },

    // Ganti deleteNewTask untuk memperbarui dropdown dengan benar
    deleteNewTask(key) {
      const taskIndex = this.newTasks.findIndex(task => task.key === key);
      if (taskIndex > -1) {
        this.newTasks.splice(taskIndex, 1);

        this.$delete(this.taskCodeSelections, key);
        this.$delete(this.taskInputs, key);
        this.$delete(this.selectBoxKeys, key);

        // Force update semua dropdown yang tersisa
        this.$nextTick(() => {
          Object.keys(this.selectBoxKeys).forEach(key => {
            this.$set(this.selectBoxKeys, key, (this.selectBoxKeys[key] || 0) + 1);
          });
        });

        this.sortTasksByPosition();
      }
    },

    sortTasksByPosition() {
      // Sort default tasks
      this.defaultTasks.sort((a, b) => a.position - b.position);

      // Sort new tasks
      this.newTasks.sort((a, b) => a.position - b.position);
    },

    async onSubmit() {
      const tasks = this.newTasks.map(i => {
        return {
          task_code_id: i.id,
          title: i.title,
          description: i.description
        };
      });

      const params = {
        id: this.target.id,
        target: {
          target_tasks_attributes: [...tasks, ...this.softDeleteTasks]
        }
      };

      this.submitLoading = true;
      const { success } = await this.updateTarget(params);
      this.submitLoading = false;
      if (!success) return;

      const title = "Task updated";
      const message = "Success! The task has been updated.";
      this.showToast({ title, message, type: "success" });
      this.$emit("close-modal");
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/clients/target/modal/EditTargetTaskDetailsModal.vue